import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { jwtDecode } from "jwt-decode";

export interface UserProps {
  password: string;
  email: string;
}

export interface FormPassword {
  password: string;
  confirmPassword: string;
}

export interface Office {
  name: string;
  email: string;
  password: string;
  cnpj: string;
  area: string;
  photo: string;
  user: {
    id: number;
    createdDate: string;
    updatedDate: string;
    name: string;
    email: string;
    password: string;
    type: number;
  };
}

export interface UserToken {
  unique_name: string;
  UserName: string;
  UserId: any;
  Type: "Customer" | "Office";
  OfficeId: number;
  email?: string;
}

type UserTypeCheck = 1 | 2 | 3;

export default function useAuth() {
  const [data, setData] = useState<UserProps>({ password: "", email: "" });
  const [formPassword, setFormPassword] = useState<FormPassword>({
    password: "",
    confirmPassword: "",
  });
  const [authenticated, setAuthenticated] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const { setLoading } = useContext(LoadingContext);
  const tokenLocal = localStorage.getItem("token");

  let objUserTk: UserToken;
  if (tokenLocal) {
    objUserTk = jwtDecode(tokenLocal) as UserToken;
  } else {
    objUserTk = {} as UserToken;
  }

  const [selectTypeUser, setSelectTypeUser] = useState<UserTypeCheck>(
    (parseInt(objUserTk.Type) as UserTypeCheck) || 1
  );

  const navigate = useNavigate();
  const showToast = useToast();

  async function handleLogin({ email, password }: UserProps) {
    try {
      const data = await authenticateUser(email, password);

      if (data) {
        setAuthenticated(true);
        navigate("/admin/index");
      }
    } catch (error: any) {
      showToast(
        "error",
        error?.response?.data?.error?.message ||
          "Erro ao efetuar o login. Por favor, tente novamente mais tarde."
      );
    }
  }

  function encodeToken(token: string): string {
    return encodeURIComponent(token);
  }

  async function decryptToken(token: string) {
    try {
      const encodedToken = encodeToken(token);
      const response = await api.get(
        `/security/decrypt_token?token=${encodedToken}`
      );
      api.defaults.headers.Authorization = `Bearer ${response.data.data.token}`;

      return response;
    } catch (error: any) {
      console.error("Erro ao descriptografar o token:", error);
      throw error;
    }
  }

  async function decryptTokenPass(token: string) {
    try {
      const response = await api.get(
        `/security/decrypt_pass_token?token=${token}`
      );
      api.defaults.headers.Authorization = `Bearer ${response.data.data.token}`;

      return response;
    } catch (error: any) {
      console.error("Erro ao descriptografar o token:", error);
      throw error;
    }
  }

  function handleLoginError(error: any) {
    if (error.response && error.response.status === 401) {
      showToast("error", "Credenciais inválidas. Por favor, tente novamente.");
    } else if (error.response && error.response.status === 403) {
      showToast(
        "error",
        "Erro ao efetuar o login. Usuário com Status Pendente."
      );
    } else {
      showToast(
        "error",
        "Erro ao efetuar o login. Por favor, tente novamente mais tarde."
      );
    }
    console.error("Erro ao efetuar o login:", error);
  }

  async function authenticateUser(
    email: string,
    password: string
  ): Promise<any> {
    const response = await api.post("/users/authentication", {
      email,
      password,
    });

    const { token } = response.data.data;
    if (token) {
      objUserTk.email = email;
      localStorage.setItem("token", token);
      api.defaults.headers.Authorization = `Bearer ${token}`;
    }
    return token;
  }

  async function sendEmailResetPassword(email: string): Promise<any> {
    try {
      const response = await api.post(`/users/password/reset?email=${email}`);
      showToast("success", "E-mail enviado com sucesso.");
    } catch (error: any) {
      showToast(
        "error",
        error?.response?.data?.error?.message ||
          "Erro ao enviar e-mail. Por favor, tente novamente."
      );
    }
  }

  async function resetPassword(userId: string, password: string): Promise<any> {
    try {
      const response = await api.patch("/users/password/change", {
        userId,
        password,
      });

      showToast(
        "success",
        "Senha alterada com sucesso! Redirecionando para o login."
      );
      setTimeout(() => {
        navigate("/auth/login");
      }, 3000);

      api.defaults.headers.Authorization = null;
    } catch (error: any) {
      showToast(
        "error",
        error?.response?.data?.error?.message ||
          "Erro ao resetar senha. Por favor, tente novamente."
      );
    }
  }

  async function activeUser(userId: string): Promise<any> {
    try {
      const response = await api.patch("/users/active", `"${userId}"`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Exibir notificação de sucesso ou redirecionar o usuário
    } catch (error: any) {
      showToast(
        "error",
        error?.response?.data?.error?.message ||
          "Erro ao ativar usuário. Por favor, tente novamente."
      );
      navigate("/auth/login");
    }
  }

  async function handleLogout() {
    setAuthenticated(false);
    localStorage.removeItem("token");
    api.defaults.headers.Authorization = null;
    navigate("/auth/login");
  }

  useEffect(() => {
    if (tokenLocal) {
      api.defaults.headers.Authorization = `Bearer ${tokenLocal}`;
      setAuthenticated(true);
    }
    setLoadingButton(false);

    if (objUserTk?.Type === "Customer") {
      setSelectTypeUser(2);
    }
  }, [tokenLocal]);

  return {
    authenticated,
    loadingButton,
    handleLogin,
    handleLogout,
    data,
    objUserTk,
    selectTypeUser,
    setSelectTypeUser,
    formPassword,
    setFormPassword,
    decryptToken,
    sendEmailResetPassword,
    resetPassword,
    decryptTokenPass,
    activeUser,
  };
}
